import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { isStandalone } from '../../lib/appRefresh';

// Pull-to-refresh for the installed (home screen) app, which has no browser
// refresh. The page scrolls the window, so a pull only starts when the window
// is at the very top and the finger isn't inside something that scrolls on
// its own (a list, a drawer, a modal — anything position: fixed), and never
// while a field has focus. Desktop never fires touch events, so this is
// inert there.

const THRESHOLD = 70; // px of indicator travel that commits a refresh
const MAX = 110;
const MIN_SPIN_MS = 600;

const EDITABLE = 'input, textarea, select, [contenteditable="true"], [contenteditable=""]';

function blocksPull(target: EventTarget | null): boolean {
  if (document.body.style.overflow === 'hidden') return true; // scroll-locked by an open dialog
  // A refresh remounts the page, so never start one over a half-filled form
  if (document.activeElement?.matches(EDITABLE)) return true;
  if (target instanceof Element && target.closest(EDITABLE)) return true;
  if (document.querySelector('[aria-modal="true"], .ck-op-overlay')) return true;
  for (let el = target instanceof Element ? target : null; el && el !== document.body; el = el.parentElement) {
    const cs = getComputedStyle(el);
    if (cs.position === 'fixed') return true;
    if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') && el.scrollTop > 0) return true;
  }
  return false;
}

export default function PullToRefresh({ onRefresh }: { onRefresh: () => Promise<void> | void }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  useEffect(() => {
    if (!isStandalone()) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let pulling = false;
    let dist = 0;

    const onStart = (e: TouchEvent) => {
      tracking = false;
      pulling = false;
      dist = 0;
      if (refreshingRef.current || e.touches.length !== 1 || window.scrollY > 0) return;
      if (blocksPull(e.target)) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      tracking = true;
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (!pulling) {
        if (dy < 0 || Math.abs(dx) > Math.abs(dy)) { tracking = false; return; }
        if (dy < 8) return;
        if (window.scrollY > 0) { tracking = false; return; }
        pulling = true;
      }
      // resistance: the indicator moves at half the finger's speed
      dist = Math.min(MAX, Math.max(0, (dy - 8) * 0.5));
      setPull(dist);
    };

    const onEnd = async () => {
      if (!pulling) { tracking = false; return; }
      const commit = dist >= THRESHOLD;
      tracking = false;
      pulling = false;
      dist = 0;
      setPull(0);
      if (!commit) return;
      refreshingRef.current = true;
      setRefreshing(true);
      try {
        await Promise.all([onRefreshRef.current(), new Promise((r) => setTimeout(r, MIN_SPIN_MS))]);
      } finally {
        refreshingRef.current = false;
        setRefreshing(false);
      }
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  if (!pull && !refreshing) return null;

  const progress = Math.min(1, pull / THRESHOLD);
  const offset = (refreshing ? THRESHOLD : pull) * 0.9;

  return (
    <div
      data-pull-refresh
      aria-live="polite"
      aria-label={refreshing ? 'Refreshing' : undefined}
      style={{
        position: 'fixed',
        top: 'calc(var(--sat) - 40px)',
        left: '50%',
        zIndex: 34, // under the status-bar strip (35), so it slides out from beneath it
        transform: `translate(-50%, ${offset}px)`,
        transition: pull ? 'none' : 'transform var(--dur-base) var(--ease-out)',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: 'var(--radius-pill)',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-hairline)',
          boxShadow: 'var(--shadow-pop)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: progress >= 1 || refreshing ? 'var(--accent-text)' : 'var(--text-muted)',
          opacity: refreshing ? 1 : 0.4 + progress * 0.6,
        }}
      >
        <RefreshCw
          size={16}
          className={refreshing ? 'animate-spin' : undefined}
          style={refreshing ? undefined : { transform: `rotate(${progress * 270}deg)` }}
        />
      </div>
    </div>
  );
}
